import { Page } from "@/types/Page";
import { apiDelete, apiGet, apiPut, apiPost } from "../_apiShim";

const BASE_PATH = "/api/v1/pages/";

// Backend's PageValidation pattern is `^[a-z0-9-]{1,80}$` — no leading slash. The
// cockpit UI keeps the legacy `/foo` convention everywhere (route construction,
// page.slug === "/login" guards, etc.). Strip on the way out, prepend on the way
// in so the rest of the codebase doesn't need to know.
function stripLeadingSlash(slug: string): string {
  return slug.startsWith("/") ? slug.slice(1) : slug;
}
function ensureLeadingSlash(slug: string | undefined): string | undefined {
  if (slug === undefined || slug === null) return slug;
  return slug.startsWith("/") ? slug : "/" + slug;
}
function rehydratePage<T extends { slug?: string }>(page: T): T {
  if (page && typeof page === "object" && page.slug !== undefined) {
    return { ...page, slug: ensureLeadingSlash(page.slug) };
  }
  return page;
}

/**
 * Create multiple pages
 * @param eventId The ID of the event
 * @param pages Array of page objects to create
 * @returns Promise resolving to an array of created page IDs
 */
export async function createPages(
  eventId: string,
  pages: Page[]
): Promise<string[]> {
  const response = await apiPost<string>(
    `${BASE_PATH}${eventId}`,
    pages.map((page) => ({
      pageName: page.pageName,
      slug: page.slug ? stripLeadingSlash(page.slug) : page.slug,
      active: page.active,
      websiteJson: page.websiteJson,
    })),
    {
      "Cache-Control": "no-store",
    }
  );

  // Handle the response which might be a JSON string
  if (typeof response === "string") {
    try {
      // Try to parse the string as JSON if it looks like a JSON array
      if (response.startsWith("[") && response.endsWith("]")) {
        const parsed = JSON.parse(response);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }

      // If it's a single ID wrapped in square brackets
      if (response.startsWith("[") && response.endsWith("]")) {
        const trimmed = response.substring(1, response?.length - 1);
        const ids = trimmed.split(",").map((id) => id.trim().replace(/"/g, ""));
        return ids;
      }

      // Just return as a single ID in an array
      return [response.replace(/"/g, "").replace(/[\[\]]/g, "")];
    } catch {
      // If parsing fails, return the string as is in an array
      return [response.replace(/"/g, "").replace(/[\[\]]/g, "")];
    }
  }

  // If the response is already an array, return it directly
  if (Array.isArray(response)) {
    return response;
  }

  // If none of the above, return an empty array
  return [];
}

/**
 * Get all pages for an event
 * @param eventId The ID of the event
 * @param fields Optional comma-separated list of fields to include
 * @returns Promise resolving to an array of Page objects
 */
export async function getPages(
  eventId: string,
  fields?: string
): Promise<Page[]> {
  const queryParams = fields ? `?fields=${encodeURIComponent(fields)}` : "";
  const pages = await apiGet<Page[]>(
    `${BASE_PATH}${eventId}${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );
  return Array.isArray(pages) ? pages.map(rehydratePage) : pages;
}

/**
 * Get a page by ID
 * @param eventId The ID of the event
 * @param pageId The ID of the page to retrieve
 * @param fields Optional comma-separated list of fields to include
 * @returns Promise resolving to a Page object
 */
export async function getPage(
  eventId: string,
  pageId: string,
  fields?: string
): Promise<Page> {
  const queryParams = fields ? `?fields=${encodeURIComponent(fields)}` : "";
  const page = await apiGet<Page>(
    `${BASE_PATH}${eventId}/${pageId}${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );
  return rehydratePage(page);
}

/**
 * Get a page by slug
 * @param eventId The ID of the event
 * @param slug The slug of the page to retrieve
 * @param fields Optional comma-separated list of fields to include
 * @returns Promise resolving to a Page object
 */
export async function getPageBySlug(
  eventId: string,
  slug: string,
  fields?: string
): Promise<Page> {
  const queryParams = fields ? `?fields=${encodeURIComponent(fields)}` : "";
  const page = await apiGet<Page>(
    `${BASE_PATH}${eventId}/by-slug/${encodeURIComponent(stripLeadingSlash(slug))}${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );
  return rehydratePage(page);
}

/**
 * Update a page
 * @param eventId The ID of the event
 * @param pageId The ID of the page to update
 * @param page The page object with updated values
 * @returns Promise that resolves when the update is complete
 */
export async function updatePage(
  eventId: string,
  pageId: string,
  page: Page
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}${eventId}/${pageId}`,
    {
      pageName: page.pageName,
      slug: page.slug ? stripLeadingSlash(page.slug) : page.slug,
      active: page.active,
      websiteJson: page.websiteJson,
      // Per-page SEO (B-6). Audit fix: these were silently dropped here,
      // making the PEditPage SEO section dead on the wire.
      seoTitle: page.seoTitle,
      seoDescription: page.seoDescription,
      ogImageUrl: page.ogImageUrl,
    },
    {
      "Cache-Control": "no-store",
    },
  );
}

/**
 * Duplicate a page
 * @param eventId The ID of the event
 * @param pageId The ID of the page to duplicate
 * @returns Promise resolving to the new page ID
 */
export async function duplicatePage(
  eventId: string,
  pageId: string
): Promise<string> {
  const response = await apiPost<string>(
    `${BASE_PATH}${eventId}/${pageId}/duplicate`,
    {},
    {
      "Cache-Control": "no-store",
    }
  );

  if (typeof response === "string") {
    return response.replace(/"/g, "");
  }

  return "";
}

/**
 * Delete a page
 * @param eventId The ID of the event
 * @param pageId The ID of the page to delete
 * @returns Promise that resolves when the deletion is complete
 */
export async function deletePage(
  eventId: string,
  pageId: string
): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${eventId}/${pageId}`, {
    "Cache-Control": "no-store",
  });
}

/**
 * Delete all pages for an event
 * @param eventId The ID of the event
 * @returns Promise that resolves when the deletion is complete
 */
export async function deleteAllPages(
  eventId: string
): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${eventId}`, {
    "Cache-Control": "no-store",
  });
}

/**
 * Check if a page exists
 * @param eventId The ID of the event
 * @param pageId The ID of the page to check
 * @returns Promise resolving to a boolean indicating whether the page exists
 */
export async function pageExists(
  eventId: string,
  pageId: string
): Promise<boolean> {
  return await apiGet<boolean>(
    `${BASE_PATH}${eventId}/${pageId}/exists`,
    {
      "Cache-Control": "no-store",
    },
    true
  );
}
